import { z } from "zod";

import { internalAccountTypeSchema } from "../lookup/schemas";

export const createEmployeeRequestSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  username: z.string().min(1),
  team: z.enum([
    "ops",
    "marketing",
    "design",
    "motion-design-contractors",
    "cx-agent-contractors",
    "pm",
    "eng",
    "data-science",
    "recruiting",
    "contractors-interns",
  ]),
});

const onboardingStepStatusSchema = z.enum(["completed", "skipped", "failed"]);

const onboardingStepResultSchema = z.object({
  step: z.enum(["create_user", "add_statsig_segment", "add_oasis_org"]),
  status: onboardingStepStatusSchema,
  message: z.string().optional(),
});

export const createEmployeeResponseSchema = z.object({
  userId: z.string(),
  workosUserId: z.string(),
  email: z.string(),
  name: z.string(),
  allCompleted: z.boolean(),
  steps: z.array(onboardingStepResultSchema),
});

export type CreateEmployeeRequest = z.infer<typeof createEmployeeRequestSchema>;
export type CreateEmployeeResponse = z.infer<typeof createEmployeeResponseSchema>;
export type OnboardingStepStatus = z.infer<typeof onboardingStepStatusSchema>;

const flaggedAccountSchema = z.object({
  userId: z.string(),
  email: z.string().nullish(),
  name: z.string().nullish(),
  internalAccountType: internalAccountTypeSchema.nullish(),
});

const activeEmployeeSchema = z.object({
  workosUserId: z.string(),
  email: z.string(),
  name: z.string().nullish(),
  captionsUserId: z.string().nullish(),
  status: z.literal("active"),
  attachedAccounts: z.array(flaggedAccountSchema),
});

const departedReasonSchema = z.enum(["main-not-member", "main-inactive"]);

const departedMainSchema = z.object({
  mainUserId: z.string(),
  mainEmail: z.string().nullish(),
  mainAccount: flaggedAccountSchema.nullish(),
  reason: departedReasonSchema,
  attachedAccounts: z.array(flaggedAccountSchema),
});

export const employeeLinksOverviewResponseSchema = z.object({
  activeEmployees: z.array(activeEmployeeSchema),
  unattached: z.array(flaggedAccountSchema),
  departedMains: z.array(departedMainSchema),
});

export const createEmployeeLinkResponseSchema = z.object({
  link: z.object({}).passthrough(),
});

export const deleteEmployeeLinkResponseSchema = z.object({
  deleted: z.boolean(),
});

export const offboardEmployeeResponseSchema = z.object({
  offboardedUserIds: z.array(z.string()),
});

export const addToOrgResponseSchema = z.object({
  workosUserId: z.string(),
  alreadyMember: z.boolean(),
});

export type FlaggedAccount = z.infer<typeof flaggedAccountSchema>;
export type ActiveEmployee = z.infer<typeof activeEmployeeSchema>;
export type DepartedMain = z.infer<typeof departedMainSchema>;
